/**
 * Media building blocks of the Mediathek editors: upload slots (audio / video
 * file, image, external video link), the media stage (preview of what the
 * website plays) and table thumbnails. Render inside <EditorShell>
 * (MediathekEditorLayout.jsx), which scopes MediathekEditor.scss.
 *
 * Upload state objects come from useMediathekUpload().getUpload(slot):
 * { busy, progress, phase: 'upload'|'finalize'|null, error }.
 */
import React, { useCallback, useId, useRef, useState } from 'react';
import { MDBBtn, MDBIcon, MDBInput, MDBProgress, MDBProgressBar } from 'mdb-react-ui-kit';
import ReactPlayer from 'react-player';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  acceptAttribute,
  isValidHttpUrl,
  mediaFileName,
  mediaFileUrl,
  mediathekImageUrl,
  stagePreviewSource,
  urlHost,
  videoEmbedUrl,
  videoProvider,
  youtubeThumbnailUrl,
} from '../../utils/mediathek';

const KIND_ICON = { audio: 'music', video: 'film', image: 'image' };

/** Runs after the next paint (focus a control that is rendered by the current update). */
const afterPaint = (fn) => {
  if (typeof window !== 'undefined' && window.requestAnimationFrame) window.requestAnimationFrame(fn);
  else setTimeout(fn, 0);
};

/**
 * Shared dropzone setup: a single file, no accept filter on drop (the upload
 * hook validates type and size with precise messages), `accept` on the file
 * dialog, ignored while busy / disabled.
 *
 * `disabled` is deliberately NOT forwarded to useDropzone: a disabled dropzone
 * removes its root drop handlers, and react-dropzone's document-level guard
 * skips drops inside its own root - so a file dropped on a busy slot would be
 * opened by the browser (leaving the page, aborting the upload / save). The
 * handlers stay live (they always preventDefault) and the drop is ignored here.
 *
 * useFsAccessApi: false keeps the native <input accept=...> dialog, which
 * filters by type in every browser; showOpenFilePicker (Chromium) ignores the
 * input's accept attribute and would show all files.
 */
function useSlotDropzone({ onPick, disabled, noClick = true, noKeyboard = true }) {
  const { t } = useTranslation();
  const [dropError, setDropError] = useState(null);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const onDrop = useCallback((accepted, rejected) => {
    if (disabledRef.current) return;
    if (rejected && rejected.length > 0) {
      setDropError(t('Please drop only one file.'));
      return;
    }
    setDropError(null);
    if (accepted && accepted[0]) onPick(accepted[0]);
  }, [onPick, t]);
  const dropzone = useDropzone({
    onDrop,
    multiple: false,
    noClick: noClick || disabled,
    noKeyboard: noKeyboard || disabled,
    useFsAccessApi: false,
  });
  const openDialog = () => {
    if (disabledRef.current) return;
    setDropError(null);
    if (typeof dropzone.open === 'function') dropzone.open();
  };
  return { ...dropzone, isDragActive: dropzone.isDragActive && !disabled, openDialog, dropError };
}

/** Thin progress bar + "Uploading... 42%" / "Processing upload...", and the upload error. */
export function UploadProgress({ upload, className = '' }) {
  const { t } = useTranslation();
  if (!upload || (!upload.busy && !upload.error)) return null;
  const progress = Math.max(0, Math.min(100, Number(upload.progress) || 0));
  const finalizing = upload.phase === 'finalize';
  return (
    <div className={`me-progress ${className}`.trim()}>
      {upload.busy && (
        <>
          <MDBProgress height={4}>
            <MDBProgressBar
              width={progress}
              valuenow={progress}
              valuemin={0}
              valuemax={100}
              aria-label={finalizing ? t('Processing upload...') : t('Uploading...')}
            />
          </MDBProgress>
          <div className="me-progress-text">
            {finalizing ? t('Processing upload...') : `${t('Uploading...')} ${progress}%`}
          </div>
          {/* Announce phase changes only - the percentage is on the progressbar (aria-valuenow) */}
          <span className="visually-hidden" aria-live="polite">
            {finalizing ? t('Processing upload...') : t('Uploading...')}
          </span>
        </>
      )}
      {upload.error && <div className="me-error-text" role="alert">{t(upload.error)}</div>}
    </div>
  );
}

/** File name linked to the stored file (opens in a new tab). */
function FileLink({ url, name }) {
  const { t } = useTranslation();
  if (!url) return <span title={name}>{name}</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title={name}>
      {name}
      <span className="visually-hidden"> ({t('opens in a new tab')})</span>
    </a>
  );
}

/** Icon button that clears a slot (the stored file is kept). */
function RemoveButton({ label, onClick, disabled, describedBy }) {
  return (
    <MDBBtn
      color="link"
      size="sm"
      type="button"
      className="me-icon-btn"
      aria-label={label}
      title={label}
      aria-describedby={describedBy}
      onClick={onClick}
      disabled={disabled}
    >
      <MDBIcon fas icon="times" aria-hidden="true" />
    </MDBBtn>
  );
}

/**
 * Audio or video file slot (row): icon, label, file name / "Not set", Upload /
 * Replace + Remove, drop target, progress and errors.
 * @param {{kind: 'audio'|'video', file: object|null, upload: object, onPick: (file: File) => void, onRemove: () => void, hint?: string, label?: string, disabled?: boolean}} props
 */
export function MediaSlot({ kind, file, upload, onPick, onRemove, hint, label, disabled = false }) {
  const { t } = useTranslation();
  const uid = useId();
  const busy = Boolean(upload?.busy);
  const primaryRef = useRef(null);
  const { getRootProps, getInputProps, isDragActive, openDialog, dropError } = useSlotDropzone({ onPick, disabled: busy || disabled });
  const url = mediaFileUrl(file);
  const hasFile = Boolean(file && url);
  const name = hasFile ? mediaFileName(file) || t('Uploaded file') : '';
  const slotLabel = label || (kind === 'audio' ? t('Audio') : t('Video file'));
  const labelId = `${uid}-label`;
  const keepId = `${uid}-keep`;

  let state = <span>{t('Not set')}</span>;
  if (isDragActive) state = <span>{t('Drop to upload')}</span>;
  else if (hasFile) state = <FileLink url={url} name={name} />;

  return (
    <div
      {...getRootProps({
        className: `me-slot${isDragActive ? ' is-drag' : ''}${busy ? ' is-busy' : ''}`,
        role: 'group',
        'aria-labelledby': labelId,
        'aria-busy': busy || undefined,
      })}
    >
      <input {...getInputProps({ accept: acceptAttribute(kind) })} />
      <div className="me-slot-row">
        <span className="me-slot-icon" aria-hidden="true">
          <MDBIcon fas icon={KIND_ICON[kind] || 'file'} />
        </span>
        <div className="me-slot-body">
          <span className="me-slot-label" id={labelId}>{slotLabel}</span>
          <span className="me-slot-state">{state}</span>
        </div>
        <div className="me-slot-actions">
          <MDBBtn
            ref={primaryRef}
            color="primary"
            outline
            size="sm"
            type="button"
            onClick={openDialog}
            disabled={busy || disabled}
            aria-describedby={hint ? `${uid}-hint` : undefined}
          >
            {hasFile ? t('Replace') : t('Upload')}
          </MDBBtn>
          {hasFile && (
            <RemoveButton
              label={t('Remove')}
              describedBy={keepId}
              disabled={busy || disabled}
              onClick={() => {
                onRemove();
                afterPaint(() => primaryRef.current?.focus());
              }}
            />
          )}
        </div>
      </div>
      {hint && (
        <div className={hasFile ? 'visually-hidden' : 'me-slot-hint'} id={`${uid}-hint`}>{hint}</div>
      )}
      <span className="visually-hidden" id={keepId}>{t('Removing only clears the field; the file stays in storage.')}</span>
      {dropError && <div className="me-error-text" role="alert">{dropError}</div>}
      <UploadProgress upload={upload} />
    </div>
  );
}

/**
 * Image slot with alternative text.
 * - variant 'row' (item media list): 40px thumbnail tile, label, file name, Upload/Replace + Remove,
 *   alt text input below when an image is set.
 * - variant 'tile' (series cover): large preview drop tile (click / Enter / Space opens the dialog)
 *   with `aspect` ('16/9' | '1/1'), file name + Remove, alt text input.
 * @param {{image: {fileName: string, alt?: string}|null, upload: object, onPick: (file: File) => void, onRemove: () => void, onAltChange: (alt: string) => void, aspect?: '16/9'|'1/1', label?: string, hint?: string, variant?: 'row'|'tile', disabled?: boolean}} props
 */
export function ImageSlot({
  image,
  upload,
  onPick,
  onRemove,
  onAltChange,
  aspect = '16/9',
  label,
  hint,
  variant = 'row',
  disabled = false,
}) {
  const { t } = useTranslation();
  const uid = useId();
  const busy = Boolean(upload?.busy);
  const isTile = variant === 'tile';
  const primaryRef = useRef(null);
  const tileRef = useRef(null);
  const [failedUrl, setFailedUrl] = useState(null);
  const { getRootProps, getInputProps, isDragActive, openDialog, dropError } = useSlotDropzone({
    onPick,
    disabled: busy || disabled,
    noClick: !isTile,
    noKeyboard: !isTile,
  });
  const fileName = image && typeof image.fileName === 'string' ? image.fileName : '';
  const hasImage = Boolean(fileName);
  const url = hasImage ? mediathekImageUrl(fileName) : '';
  const showPreview = hasImage && failedUrl !== url;
  const slotLabel = label || t('Image');
  const labelId = `${uid}-label`;
  const hintId = `${uid}-hint`;

  const handleRemove = () => {
    onRemove();
    afterPaint(() => (isTile ? tileRef.current : primaryRef.current)?.focus());
  };

  const altInput = hasImage && (
    <div className="me-slot-extra">
      <MDBInput
        id={`${uid}-alt`}
        label={t('Alternative text')}
        value={image.alt || ''}
        disabled={disabled}
        onChange={(e) => onAltChange(e.target.value)}
      />
    </div>
  );

  let state = <span>{t('Not set')}</span>;
  if (isDragActive) state = <span>{t('Drop to upload')}</span>;
  else if (hasImage) state = <FileLink url={url} name={fileName} />;

  if (isTile) {
    const rootProps = getRootProps({
      className: `me-tile${hasImage ? ' has-image' : ''}${isDragActive ? ' is-drag' : ''}${busy || disabled ? ' is-disabled' : ''}`,
      role: 'button',
      'aria-label': hasImage ? `${slotLabel}: ${t('Replace image')}` : `${slotLabel}: ${t('Upload image')}`,
      'aria-describedby': hint ? hintId : undefined,
      'aria-disabled': busy || disabled || undefined,
      style: { aspectRatio: aspect === '1/1' ? '1 / 1' : '16 / 9' },
    });
    const { ref: dropRootRef } = rootProps;
    return (
      <div className="me-image-tile">
        {label && <span className="me-slot-label mb-2" id={labelId}>{slotLabel}</span>}
        <div
          {...rootProps}
          ref={(node) => {
            tileRef.current = node;
            if (typeof dropRootRef === 'function') dropRootRef(node);
            else if (dropRootRef) dropRootRef.current = node;
          }}
        >
          <input {...getInputProps({ accept: acceptAttribute('image') })} />
          {showPreview ? (
            <img src={url} alt={image.alt || ''} onError={() => setFailedUrl(url)} />
          ) : (
            <div className="me-tile-empty">
              <span className="me-stage-glyph" aria-hidden="true"><MDBIcon fas icon="image" /></span>
              <span>{hasImage ? fileName : t('Drop an image here or click to choose one')}</span>
            </div>
          )}
          {isDragActive && <div className="me-tile-drop">{t('Drop to upload')}</div>}
        </div>
        <div className="me-tile-meta">
          <span className="me-slot-state">{state}</span>
          {hasImage && (
            <RemoveButton label={t('Remove image')} onClick={handleRemove} disabled={busy || disabled} describedBy={`${uid}-keep`} />
          )}
        </div>
        {hint && <div className="me-help" id={hintId}>{hint}</div>}
        <span className="visually-hidden" id={`${uid}-keep`}>{t('Removing only clears the field; the file stays in storage.')}</span>
        {dropError && <div className="me-error-text" role="alert">{dropError}</div>}
        <UploadProgress upload={upload} />
        {altInput}
      </div>
    );
  }

  return (
    <div
      {...getRootProps({
        className: `me-slot${isDragActive ? ' is-drag' : ''}${busy ? ' is-busy' : ''}`,
        role: 'group',
        'aria-labelledby': labelId,
        'aria-busy': busy || undefined,
      })}
    >
      <input {...getInputProps({ accept: acceptAttribute('image') })} />
      <div className="me-slot-row">
        <span className="me-slot-icon" aria-hidden="true">
          {showPreview ? <img src={url} alt="" onError={() => setFailedUrl(url)} /> : <MDBIcon fas icon="image" />}
        </span>
        <div className="me-slot-body">
          <span className="me-slot-label" id={labelId}>{slotLabel}</span>
          <span className="me-slot-state">{state}</span>
        </div>
        <div className="me-slot-actions">
          <MDBBtn
            ref={primaryRef}
            color="primary"
            outline
            size="sm"
            type="button"
            onClick={openDialog}
            disabled={busy || disabled}
            aria-describedby={hint ? hintId : undefined}
          >
            {hasImage ? t('Replace') : t('Upload')}
          </MDBBtn>
          {hasImage && (
            <RemoveButton label={t('Remove image')} onClick={handleRemove} disabled={busy || disabled} describedBy={`${uid}-keep`} />
          )}
        </div>
      </div>
      {hint && <div className={hasImage ? 'visually-hidden' : 'me-slot-hint'} id={hintId}>{hint}</div>}
      <span className="visually-hidden" id={`${uid}-keep`}>{t('Removing only clears the field; the file stays in storage.')}</span>
      {dropError && <div className="me-error-text" role="alert">{dropError}</div>}
      <UploadProgress upload={upload} />
      {altInput}
    </div>
  );
}

/**
 * External video (YouTube / Vimeo) slot: host as state line, URL input with the
 * existing validation messages, the "external video wins" hint and the embed URL.
 * Collapsed (with "Add link") while empty.
 * @param {{value: string, onChange: (url: string) => void, label?: string, disabled?: boolean}} props
 */
export function ExternalVideoSlot({ value, onChange, label, disabled = false }) {
  const { t } = useTranslation();
  const uid = useId();
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const addRef = useRef(null);
  const url = (value || '').trim();
  const expanded = open || Boolean(url) || Boolean(value);
  const embed = videoEmbedUrl(url);
  const invalid = Boolean(url) && !isValidHttpUrl(url);
  const unsupported = Boolean(url) && !invalid && !embed;
  const provider = embed ? videoProvider({ externalVideoUrl: url }) : null;
  let icon = <MDBIcon fas icon="link" />;
  if (provider === 'youtube') icon = <MDBIcon fab icon="youtube" />;
  else if (provider === 'vimeo') icon = <MDBIcon fab icon="vimeo-v" />;
  const labelId = `${uid}-label`;
  const messageId = `${uid}-message`;

  return (
    <div className="me-slot" role="group" aria-labelledby={labelId}>
      <div className="me-slot-row">
        <span className="me-slot-icon" aria-hidden="true">{icon}</span>
        <div className="me-slot-body">
          <span className="me-slot-label" id={labelId}>{label || t('Video link')}</span>
          <span className="me-slot-state" title={url || undefined}>{url ? urlHost(url) || url : t('Not set')}</span>
        </div>
        <div className="me-slot-actions">
          {!expanded && (
            <MDBBtn
              ref={addRef}
              color="primary"
              outline
              size="sm"
              type="button"
              disabled={disabled}
              onClick={() => {
                setOpen(true);
                afterPaint(() => inputRef.current?.focus());
              }}
            >
              {t('Add link')}
            </MDBBtn>
          )}
          {url && (
            <RemoveButton
              label={t('Remove link')}
              disabled={disabled}
              onClick={() => {
                onChange('');
                setOpen(false);
                afterPaint(() => addRef.current?.focus());
              }}
            />
          )}
        </div>
      </div>
      {expanded && (
        <div className="me-slot-extra">
          <MDBInput
            ref={inputRef}
            id={`${uid}-url`}
            label={t('Video URL')}
            type="url"
            inputMode="url"
            value={value || ''}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid || unsupported ? messageId : `${uid}-hint`}
            onChange={(e) => {
              setOpen(true);
              onChange(e.target.value);
            }}
          />
          {invalid && (
            <div className="me-error-text" id={messageId}>{t('The external video URL is not a valid URL')}</div>
          )}
          {unsupported && (
            <div className="me-warning-text" id={messageId}>
              {t('This URL is not recognised as a YouTube or Vimeo video; the website cannot embed it.')}
            </div>
          )}
          <div className="me-help" id={`${uid}-hint`}>
            {t('If both an external video and a video file are set, the website embeds the external video.')}
          </div>
          {embed && (
            <div className="me-help text-break">
              {t('Embed URL')}: {embed}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Wrapper of the slot rows (vertical list with consistent gaps). */
export function MediaSlotList({ children, label }) {
  return (
    <div className="me-slots" role="group" aria-label={label}>
      {children}
    </div>
  );
}

function StagePlaceholder({ icon, children, className = '' }) {
  return (
    <div className={`me-stage-placeholder ${className}`.trim()}>
      <span className="me-stage-glyph" aria-hidden="true"><MDBIcon fas icon={icon} /></span>
      {children}
    </div>
  );
}

/** CSS url() value for an inline background-image (quotes / backslashes escaped). */
const cssUrl = (url) => `url("${String(url).replace(/["\\\r\n]/g, (ch) => encodeURIComponent(ch))}")`;

/**
 * Blurred, dimmed copy of the image behind the contained stage image, so square
 * podcast covers fill the 16:9 frame without being cropped. Decorative only.
 */
function StageBackdrop({ url }) {
  if (!url) return null;
  return <div className="me-stage-backdrop" style={{ backgroundImage: cssUrl(url) }} aria-hidden="true" />;
}

/** Bottom strip with the audio player (on top of image / backdrop). */
function StageAudioBar({ url, label }) {
  return (
    <div className="me-stage-audio">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio key={url} controls preload="none" src={url} aria-label={label} />
    </div>
  );
}

/**
 * Preview of what the website plays / shows for an item (same precedence as the
 * public API, see stagePreviewSource): external video (click-to-load thumbnail),
 * video file, image with audio bar, image, audio only, or an empty state. Images
 * are shown uncropped (object-fit: contain) over a blurred backdrop of themselves.
 * The line below says which source the website uses.
 * @param {{item: object, className?: string}} props
 */
export function MediaStage({ item, className = '' }) {
  const { t } = useTranslation();
  const [failedUrl, setFailedUrl] = useState(null);
  const playerRef = useRef(null);
  const source = stagePreviewSource(item);
  const imageUrl = item?.image?.fileName ? mediathekImageUrl(item.image.fileName) : '';
  const imageOk = Boolean(imageUrl) && failedUrl !== imageUrl;
  const imageAlt = item?.image?.alt || '';
  const audioUrl = mediaFileUrl(item?.audio);
  const videoUrl = mediaFileUrl(item?.video);
  const externalUrl = (item?.externalVideoUrl || '').trim();
  const provider = source === 'external' ? videoProvider(item) : null;

  let content;
  let dark = false;
  let caption = null;
  switch (source) {
    case 'external': {
      dark = true;
      const light = (imageOk && imageUrl) || youtubeThumbnailUrl(externalUrl) || (
        <div className="me-stage-light" aria-hidden="true">
          <MDBIcon fab icon={provider === 'vimeo' ? 'vimeo-v' : 'youtube'} />
        </div>
      );
      // react-player's preview div is focusable without a role: take it out of the
      // tab order and use a real button as play icon (its click bubbles to the
      // preview). The preview unmounts on activation, so focus moves to the
      // player wrapper instead of dropping to <body>.
      content = (
        <div className="me-stage-player" ref={playerRef} tabIndex={-1}>
          <ReactPlayer
            key={externalUrl}
            url={externalUrl}
            light={light}
            playing
            controls
            width="100%"
            height="100%"
            previewTabIndex={-1}
            playIcon={(
              <button type="button" className="me-play" aria-label={t('Play video')}>
                <MDBIcon fas icon="play" aria-hidden="true" />
              </button>
            )}
            onClickPreview={() => afterPaint(() => playerRef.current?.focus())}
          />
        </div>
      );
      caption = provider === 'vimeo' ? t('The website embeds the Vimeo video.') : t('The website embeds the YouTube video.');
      break;
    }
    case 'videoFile':
      dark = true;
      content = (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video key={videoUrl} className="me-stage-video" controls preload="metadata" src={videoUrl} poster={imageOk ? imageUrl : undefined} />
      );
      caption = t('The website plays the video file.');
      break;
    case 'imageAudio':
    case 'image': {
      const withAudio = source === 'imageAudio';
      dark = imageOk;
      content = (
        <>
          {imageOk ? (
            <>
              <StageBackdrop url={imageUrl} />
              <img
                className={`me-stage-image${withAudio ? ' has-audio' : ''}`}
                src={imageUrl}
                alt={imageAlt}
                onError={() => setFailedUrl(imageUrl)}
              />
            </>
          ) : (
            <StagePlaceholder icon="image" className={withAudio ? 'has-audio' : ''}>
              <span>{t('The image could not be loaded.')}</span>
            </StagePlaceholder>
          )}
          {withAudio && <StageAudioBar url={audioUrl} label={t('Audio')} />}
        </>
      );
      caption = withAudio ? t('The website shows the image and plays the audio file.') : t('The website shows the image.');
      break;
    }
    case 'audio':
      dark = true;
      content = (
        <>
          <StagePlaceholder icon="headphones" className="has-audio">
            <span className="me-stage-file" title={mediaFileName(item?.audio) || undefined}>{mediaFileName(item?.audio)}</span>
          </StagePlaceholder>
          <StageAudioBar url={audioUrl} label={t('Audio')} />
        </>
      );
      caption = t('The website plays the audio file.');
      break;
    default:
      content = (
        <StagePlaceholder icon="photo-video">
          <span>{t('No media yet – upload audio or an image, or add a video link.')}</span>
        </StagePlaceholder>
      );
  }

  return (
    <figure className={`me-stage ${className}`.trim()}>
      <div className={`me-stage-frame ratio ratio-16x9${dark ? ' is-dark' : ''}${source === 'none' ? ' is-empty' : ''}`}>
        <div className="me-stage-inner">{content}</div>
      </div>
      {caption && (
        <figcaption className="me-stage-caption">
          <MDBIcon fas icon="globe" aria-hidden="true" />
          <span>{caption}</span>
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Square thumbnail for tables (48px, 'sm' = 40px): the Mediathek image
 * (`fileName`) or any `url`, else an icon.
 * @param {{fileName?: string, url?: string, alt?: string, icon?: string, size?: 'md'|'sm', title?: string}} props
 */
export function MediaThumb({ fileName, url, alt = '', icon = 'photo-video', size = 'md', title }) {
  const [failedUrl, setFailedUrl] = useState(null);
  const src = url || (fileName ? mediathekImageUrl(fileName) : '');
  const show = Boolean(src) && failedUrl !== src;
  return (
    <span className={`me-thumb${size === 'sm' ? ' is-sm' : ''}`} title={title}>
      {show ? (
        <img src={src} alt={alt} loading="lazy" onError={() => setFailedUrl(src)} />
      ) : (
        <MDBIcon fas icon={icon} aria-hidden="true" />
      )}
    </span>
  );
}
